import { v4 as uuidv4 } from "uuid";

import RedZone from "./red-zone";
import IdTypePoint from "./id-type-point";
import {
  CleaningConfiguration,
  CleaningConfigurationApi,
  mapCleaningConfigurationApiToCleaningConfiguration,
  mapCleaningConfigurationToApi,
} from "../cleaning-configurations/global-cleaning-configuration";
import CleaningState from "../../../enums/robot/rooms/zone-cleaning-state";
import IBaseZone, { IBaseZoneApi, mapApiPointsToPoints, mapPointsToApi } from "./base-zone";
import ZonePointType from "../../../enums/robot/rooms/zone-point-type";
import { NavigatorName } from "../../../enums/command-hub/navigator";
import RuleZone from "./rule-zone";

interface CleaningZone extends IBaseZone {
  name: string | null;
  navigator: string | null;
  startPosition: IdTypePoint | null;
  cleaningConfiguration: CleaningConfiguration;
  cleaningState: CleaningState;
  doWallFollow: boolean;
  fleetControlId: string | null;
}

export interface CleaningZoneApi extends IBaseZoneApi {
  name: string;
  navigator: string;
  startPosition: number[];
  cleaningConfiguration: CleaningConfigurationApi;
  cleaningState: CleaningState;
  doWallFollow: boolean;
  fleetControlId: string | null;
}

export const mapCleaningZoneApiToCleaningZone = (apiData: CleaningZoneApi) =>
  ({
    ...apiData,
    startPosition: apiData.startPosition != null
      ? {
        coordinateX: apiData.startPosition[0],
        coordinateY: apiData.startPosition[1],
        coordinateTheta: apiData.startPosition[2],
        id: uuidv4(),
        type:
          apiData.navigator === NavigatorName.RepeatPath
            ? ZonePointType.CleaningZoneStartPosition
            : ZonePointType.TeachAndRepeatZoneStartPosition,
      }
      : null,
    cleaningConfiguration: mapCleaningConfigurationApiToCleaningConfiguration(apiData.cleaningConfiguration),
    points: mapApiPointsToPoints(
      apiData.points,
      apiData.navigator === NavigatorName.RepeatPath
        ? ZonePointType.TeachAndRepeatZoneCorner
        : ZonePointType.CleaningZoneCorner
    ),
  } as CleaningZone);

export const mapCleaningZoneToApi = (cleaningZone: CleaningZone) =>
  ({
    ...cleaningZone,
    name: cleaningZone.name ?? "",
    navigator: cleaningZone.navigator ?? "",
    fleetControlId: cleaningZone.fleetControlId === "" ? null : cleaningZone.fleetControlId,
    startPosition: [
      cleaningZone.startPosition?.coordinateX ?? 1.0,
      cleaningZone.startPosition?.coordinateY ?? 1.0,
      cleaningZone.startPosition?.coordinateTheta ?? 0,
    ],
    cleaningConfiguration: mapCleaningConfigurationToApi(cleaningZone.cleaningConfiguration),
    points: mapPointsToApi(cleaningZone.points.map((p) => p.point)),
  } as CleaningZoneApi);

export const mapPartialCleaningZoneToApi = (cleaningZone: Partial<CleaningZone>) =>
  ({
    ...cleaningZone,
    startPosition:
      cleaningZone.startPosition !== undefined
        ? [
            cleaningZone.startPosition?.coordinateX,
            cleaningZone.startPosition?.coordinateY,
            cleaningZone.startPosition?.coordinateTheta ?? 0,
          ]
        : undefined,
    cleaningConfiguration:
      cleaningZone.cleaningConfiguration !== undefined
        ? mapCleaningConfigurationToApi(cleaningZone.cleaningConfiguration)
        : undefined,
    points: cleaningZone.points !== undefined ? mapPointsToApi(cleaningZone.points.map((p) => p.point)) : undefined,
  } as Partial<CleaningZoneApi>);

export const newCleaningZone = (name: string, navigator: string | null, roomId: string): CleaningZone => {
  
  return {
    id: uuidv4(),
    room: roomId,
    name: name,
    navigator: navigator,
    points: [],
    doWallFollow: true,
    startPosition: null,
    cleaningConfiguration: {
      spatialProperty: null,
      environmentPollution: null,
      environmentHazard: null,
      independentLegacy2601: {
        beeper: null,
        minObstacleHeightM: null,
        warningLight: null,
      },
    } as CleaningConfiguration,
    cleaningState: CleaningState.Pending,
    fleetControlId: null,
  };
};

export const isCleaningZone = (zone: CleaningZone | RedZone | RuleZone): zone is CleaningZone => {
  return "startPosition" in zone && "navigator" in zone && "cleaningConfiguration" in zone && "cleaningState" in zone;
};

export const isTeachAndRepeatZone = (zone: CleaningZone | RedZone | RuleZone): zone is CleaningZone => {
  return isCleaningZone(zone) && zone.navigator === NavigatorName.RepeatPath;
};

export const CLEANINGZONE_NAVIGATOR_REPEATPATH: string = NavigatorName.RepeatPath;

export const copyCleaningZone = (zone: CleaningZone) => {
  return {
    ...zone,
    points: [...zone.points.map((p) => ({ orderIndex: p.orderIndex, point: { ...p.point } }))],
    startPosition: zone.startPosition !== null ? { ...zone.startPosition } : null,
    cleaningConfiguration: { ...zone.cleaningConfiguration },
  };
};

export const isCleaningZoneObject = (payload: unknown): payload is CleaningZone => {
  return (
    payload !== null &&
    typeof payload === "object" &&
    "name" in payload &&
    "navigator" in payload &&
    "startPosition" in payload &&
    "cleaningConfiguration" in payload &&
    "cleaningState" in payload &&
    "doWallFollow" in payload &&
    "fleetControlId" in payload
  );
};

export default CleaningZone;
